<template>
  <Layout>
    <div class="page-container">
      <div class="mc-list-header">
        <PageHeader :title="title" subTitle="">
          <template #afterTitle>
            <Back :hasChanged="hasChanged" />
          </template>
        </PageHeader>
      </div>

      <FormulateForm
        v-model="formValues"
        :errors="formErrors"
        class="vendor-detail-form"
        name="vendor-detail-form"
        @submit="handleSubmit"
      >
        <div class="__header-container">
          <h4 class="__header-text">Informasi Vendor</h4>
          <McButton
            variant="secondary"
            :text="$t('global.cancel')"
            icon="icon-outline_close"
            @click="$refs['mc-modal-cancel'].show()"
            :disabled="!hasChanged"
          />

          <McButton
            variant="primary"
            text="Simpan"
            icon="icon-outline_save"
            @click="triggerSave"
            :disabled="!hasChanged"
          />
        </div>

        <b-card>
          <b-row>
            <b-col md="3" v-for="field in vendorDetailFields" :key="field.name">
              <FormulateInput
                v-bind="field"
                :help="field?.validation?.includes('required')"
                validation-behavior="live"
                :disabled="getFieldDisabledState(field.name)"
              >
                <template #prefix v-if="field.name == 'phone_number'">
                  <div class="formulate-input-prefix">
                    <span>+62</span>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
          </b-row>
        </b-card>
      </FormulateForm>

      <b-modal
        class="mc-modal"
        id="mc-modal-cancel"
        ref="mc-modal-cancel"
        title="Batal"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>Apakah Anda yakin ingin membatalkan perubahan <strong>Informasi Vendor</strong>?</p>
        </div>
        <template #modal-footer="{ cancel }">
          <b-row class="mc-modal__filter-footer mx-0">
            <b-col sm="12" md="12" class="mc-modal__filter-footer-btn">
              <McButton
                :text="$t('global.no')"
                @click="cancel()"
                variant="secondary"
                class="me-2"
              />
              <McButton :text="$t('global.yes')" @click="handleCancel" variant="primary" />
            </b-col>
          </b-row>
        </template>
      </b-modal>

      <div class="vendor-contracts">
        <div class="__header-container">
          <h4 class="__header-text">Daftar Kontrak</h4>
          <McButton
            variant="primary"
            :text="$t('global.create')"
            icon="icon-outline_plus"
            :disabled="isCreating"
            @click="handleAddContract"
          />
        </div>

        <b-card>
          <template v-if="isCreating">
            <VendorContractsTableEmpty />
          </template>

          <template v-else>
            <VendorContractsTable :vendorId="vendorId" />
          </template>
        </b-card>
      </div>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'

import Back from './components/Back'
import VendorContractsTable from './components/VendorContractsTable'
import VendorContractsTableEmpty from './components/VendorContractsTableEmpty'

import vendorDetailFields from './lib/fields/vendor-detail-fields'
import {
  transformVendorStoreRequest,
  transformVendorShowResponse,
  transformVendorUpdateRequest,
} from './lib/helpers/transform-data'
// TODO: use global helpers instead
import { diffObject } from './lib/helpers/compare.js'
import handleApiValidation from './lib/helpers/handle-api-validation'

import * as Vendor from '@/services/rest-api/lib/vendor/vendor'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Master Vendor Form',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    Back,
    VendorContractsTable,
    VendorContractsTableEmpty,
  },
  data() {
    return {
      formValues: {},
      formErrors: {},

      showResponse: null,

      isLoading: false,
    }
  },
  computed: {
    title() {
      return this.isCreating ? 'Tambah Vendor Baru' : 'Vendor'
    },
    isCreating() {
      return this.$route.params.id === undefined
    },
    vendorDetailFields() {
      return vendorDetailFields
    },
    hasChanged() {
      if (this.isLoading) return false

      const currentFormValues = this.formValues
      let prevFormValues = null

      // If on Create Form, previous form values is empty/default value
      if (this.isCreating) prevFormValues = { status: true }
      // Else, get previous form values from showResponse
      else prevFormValues = transformVendorShowResponse(this.showResponse, vendorDetailFields)

      const [_, hasChanged] = diffObject(prevFormValues, currentFormValues)

      return hasChanged
    },
    vendorId() {
      return this.$route.params.id
    },
  },
  watch: {
    // Reset error when formValues changes
    formValues(newVal, oldVal) {
      const errorKeys = Object.keys(this.formErrors)

      // No error, skip reset
      if (errorKeys.length == 0) return

      // Reset error if field with error is changed
      errorKeys.forEach((key) => {
        if (newVal[key] !== oldVal[key]) this.$delete(this.formErrors, key)
      })
    },
  },
  async mounted() {
    if (!this.isCreating) {
      await this.showData()
    } else {
      this.formValues = transformVendorShowResponse(null, vendorDetailFields)
    }
    this.isLoading = false
  },
  methods: {
    // Imported
    handleApiValidation,

    getFieldDisabledState(fieldName) {
      // Disable pairing code field if user want to edit and user already input pairing code before
      if (
        fieldName === 'pairing_code' &&
        !this.isCreating &&
        this.showResponse['pairing_code'] !== ''
      ) {
        return true
      }

      return this.isLoading
    },

    // Core
    async showData(id) {
      this.isLoading = true
      const res = await Vendor.show(id ?? this.vendorId)

      if (res.errors) {
        this.$mcToast.error('Gagal mengambil data vendor')
        this.isLoading = false
        return
      }

      this.showResponse = res.data
      this.formValues = transformVendorShowResponse(res.data, vendorDetailFields)
    },
    async handleSubmit(data) {
      this.isLoading = true

      let res = null
      let payload = null

      if (this.isCreating) {
        payload = transformVendorStoreRequest(data, vendorDetailFields)
        res = await Vendor.store(payload)
      } else {
        const oldData = transformVendorShowResponse(this.showResponse, vendorDetailFields)
        payload = transformVendorUpdateRequest(data, oldData, vendorDetailFields)

        res = await Vendor.update(this.vendorId, payload)
      }

      if (!res) {
        this.$mcToast.error('Gagal menyimpan data')
        this.isLoading = false
        return
      }

      if (res?.errors) {
        this.$mcToast.error('Terdapat kesalahan dalam input anda')
        this.formErrors = this.handleApiValidation(res.errors)
        this.isLoading = false
        return
      }

      const id = res.data?.id ?? this.vendorId

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            { tag: 'strong', text: data.name, class: '__blue-highlight-text' },
            { tag: 'span', text: 'berhasil disimpan' },
          ],
        },
      })

      if (this.isCreating) {
        this.$router.push({ name: 'Master Vendor Detail', params: { id } })
      }

      await this.showData(id)
      this.isLoading = false
    },

    // Utility
    handleAddContract() {
      this.$router.push({
        name: 'Master Vendor Contract Create',
        query: { vendorId: this.vendorId },
      })
    },
    handleStatusChange(isActive) {
      this.$set(this.formValues, 'status', isActive)
    },
    handleCancel() {
      this.formValues = transformVendorShowResponse(this.showResponse, vendorDetailFields)
      this.$refs['mc-modal-cancel'].hide()
    },
    triggerSave() {
      this.$formulate.submit('vendor-detail-form')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './styles/form.scss';
</style>
